import { Calendar, cn,I18nProvider } from '@heroui/react'
import { CalendarDate } from '@internationalized/date'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { DriverAutocomplete } from '@/components/common/DriverAutocomplete'
import { useTimesheetStore } from '@/store/timesheet.store'
import type { Driver } from '@/types/driver'

export function CalendarSidebar() {
  const { t } = useTranslation()
  const { filters, setFilters, selectedDay, setSelectedDay, expenses, report } = useTimesheetStore()

  const focusDate = new CalendarDate(filters.year, filters.month, 1)

  const calDate = selectedDay
    ? new CalendarDate(selectedDay.date.getFullYear(), selectedDay.date.getMonth() + 1, selectedDay.date.getDate())
    : null

  type ExpenseType = 'none' | 'by' | 'other' | 'both' | null

  // Helper to determine the type of expenses for a given day
  const getExpenseType = (dateObj: CalendarDate): ExpenseType => {
    if (!filters.driverID) return null

    const targetDateStr = `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`
    const dayExpenses = expenses.filter(exp => exp.date?.startsWith(targetDateStr))

    if (dayExpenses.length === 0) return 'none'

    const hasBy = dayExpenses.some(exp => exp.country.toLowerCase() === 'by')
    const hasOther = dayExpenses.some(exp => exp.country.toLowerCase() !== 'by')
    if (hasBy && hasOther) return 'both'
    return hasOther ? 'other' : 'by'
  }

  const isDateInActivity = (dateObj: CalendarDate): boolean => {
    if (!report?.Items) return false
    const targetDateStr = `${dateObj.year}-${String(dateObj.month).padStart(2, '0')}-${String(dateObj.day).padStart(2, '0')}`

    return report.Items.some(item => {
      if (!item.StartDate || !item.EndDate) return false
      const start = item.StartDate.split('T')[0]
      const end = item.EndDate.split('T')[0]
      return targetDateStr >= start && targetDateStr <= end
    })
  }

  const getIndicatorColor = (type: ExpenseType) => {
    if (type === 'none') return 'bg-gray-300'
    if (type === 'by') return 'bg-success'
    if (type === 'other') return 'bg-primary'
    if (type === 'both') return 'bg-purple-500'
    return 'bg-gray-300'
  }

  const getSelectionColor = (type: ExpenseType) => {
    if (type === 'none') return '!bg-gray-300 !text-default-foreground'
    if (type === 'by') return '!bg-success !text-success-foreground'
    if (type === 'other') return '!bg-primary !text-primary-foreground'
    if (type === 'both') return '!bg-purple-500 !text-accent-foreground'
    return '!bg-gray-300'
  }

  const handleDateChange = (date: CalendarDate) => {
    const isSameDate = selectedDay?.date.getFullYear() === date.year &&
      selectedDay.date.getMonth() === date.month - 1 &&
      selectedDay.date.getDate() === date.day

    if (isSameDate) {
      setSelectedDay(null)
    } else {
      const newDate = new Date(date.year, date.month - 1, date.day)
      const driverId = selectedDay?.driverId || filters.driverID
      setSelectedDay({ driverId, date: newDate })
    }
  }

  const handleFocusChange = (date: CalendarDate) => {
    if (!id) {
      setFilters({ year: date.year, month: date.month })
    }
  }

  const { id } = useParams<{ id: string }>()

  const handleDriverChange = (d: Driver | null) => {
    setFilters({ driverID: d?.ID ?? undefined })
  }

  return (
    <div className="w-[300px] flex-shrink-0 bg-content1 p-4 flex flex-col items-center gap-4">
      <div className="w-full">
        <DriverAutocomplete
          selectedId={filters.driverID}
          onSelectionChange={handleDriverChange}
          isDisabled={!!id}
        />
      </div>

      <I18nProvider locale="ru-RU">
        <Calendar
          aria-label="Event date"
          value={calDate}
          focusedValue={focusDate}
          onFocusChange={handleFocusChange}
          onChange={handleDateChange}
          minValue={new CalendarDate(2026, 1, 1)}
        >
        <Calendar.Header>
          {!id && <Calendar.NavButton className="text-foreground" slot="previous" />}
          {id ? (
            <div className="w-full flex justify-center text-sm font-semibold text-foreground py-1 select-none pointer-events-none">
              <Calendar.YearPickerTrigger className="w-full justify-center pointer-events-none cursor-default hover:bg-transparent" isDisabled>
                <Calendar.YearPickerTriggerHeading className="text-foreground" />
              </Calendar.YearPickerTrigger>
            </div>
          ) : (
            <Calendar.YearPickerTrigger className="w-full justify-center">
              <Calendar.YearPickerTriggerHeading className="text-foreground" />
              <Calendar.YearPickerTriggerIndicator className="text-foreground" />
            </Calendar.YearPickerTrigger>
          )}
          {!id && <Calendar.NavButton className="text-foreground" slot="next" />}
        </Calendar.Header>
        <Calendar.Grid>
          <Calendar.GridHeader>
            {day => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
          </Calendar.GridHeader>
          <Calendar.GridBody>
            {date => {
              const expenseType = getExpenseType(date)
              const indicatorColor = getIndicatorColor(expenseType)
              const selectionColor = getSelectionColor(expenseType)
              const isSelected = calDate && date.compare(calDate) === 0
              const hasActivity = isDateInActivity(date)

              return (
                <Calendar.Cell date={date}>
                  {({formattedDate}) => (
                    <div className={cn(
                      "relative flex items-center justify-center w-full h-full rounded-md transition-colors",
                      hasActivity && !isSelected ? "bg-primary/20 dark:bg-primary/30" : ""
                    )}
                    >
                      {formattedDate}
                      {isSelected && (
                        <Calendar.CellIndicator className={selectionColor} />
                      )}
                      {!isSelected && expenseType !== null && (
                        <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${indicatorColor}`} />
                      )}
                    </div>
                  )}
                </Calendar.Cell>
              )
            }}
          </Calendar.GridBody>
        </Calendar.Grid>
        <Calendar.YearPickerGrid>
          <Calendar.YearPickerGridBody>
            {({year}) => <Calendar.YearPickerCell year={year} />}
          </Calendar.YearPickerGridBody>
        </Calendar.YearPickerGrid>
      </Calendar>
      </I18nProvider>

      {/* Legend */}
      <div className="w-full mt-2 flex flex-col gap-2 text-xs text-foreground/70 px-2">
        <div className="flex items-center gap-2">
          <div className="w-8 flex justify-center items-center flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-gray-300" />
          </div>
          <span>{t('timesheet.legend_no_expenses')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 flex justify-center items-center flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-success" />
          </div>
          <span>{t('timesheet.legend_by_only')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 flex justify-center items-center flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
          </div>
          <span>{t('timesheet.legend_both')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 flex justify-center items-center flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
          </div>
          <span>{t('timesheet.legend_other')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 relative flex items-center justify-center rounded-md bg-accent/20 dark:bg-accent/40 text-sm font-light text-black dark:text-white flex-shrink-0">
            1
          </div>
          <span>{t('timesheet.legend_activity')}</span>
        </div>
      </div>
    </div>
  )
}
